import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { HealthIndicator } from "@/components/sistema/HealthIndicator";
import { LoadingSkeleton } from "@/components/states/LoadingSkeleton";
import { ErrorState } from "@/components/states/ErrorState";
import { EmptyState } from "@/components/states/EmptyState";
import { Timer } from "lucide-react";

type Stage = {
  stage_code: string; stage_name: string;
  p50_target_ms: number; p95_target_ms: number;
  current_p50: number; current_p95: number; sample_size: number;
};
type Slow = { turn_id: string; total_ms: number; group_id: string | null; created_at: string };

export default function SistemaLatencia() {
  const { user } = useAuth();
  const [stages, setStages] = useState<Stage[]>([]);
  const [slow, setSlow] = useState<Slow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data: m, error: mErr } = await supabase.from("org_members")
          .select("org_id").eq("user_id", user.id).limit(1).maybeSingle();
        if (mErr) throw mErr;
        if (!m || cancelled) return;
        const [{ data: s, error: sErr }, { data: t, error: tErr }] = await Promise.all([
          supabase.from("latency_budget").select("*")
            .eq("org_id", m.org_id).order("stage_code", { ascending: true }),
          supabase.from("latency_top_slow_turns")
            .select("turn_id, total_ms, group_id, created_at")
            .eq("org_id", m.org_id)
            .gte("created_at", new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())
            .order("total_ms", { ascending: false }).limit(10),
        ]);
        if (sErr) throw sErr;
        if (tErr) throw tErr;
        if (cancelled) return;
        setStages((s as Stage[]) ?? []);
        setSlow((t as Slow[]) ?? []);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Falha ao carregar latência");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Latência por etapa</h1>
        <p className="text-muted-foreground">p50 / p95 contra orçamento por estágio (E1–E8)</p>
      </div>

      {loading && <LoadingSkeleton shape="kpi" count={8} />}
      {!loading && error && <ErrorState message={error} />}
      {!loading && !error && stages.length === 0 && (
        <EmptyState
          icon={Timer}
          title="Sem orçamento de latência configurado"
          description="Os alvos por estágio (E1–E8) ainda não foram criados para esta organização."
        />
      )}

      {!loading && !error && stages.length > 0 && (
        <>
          <div className="grid gap-3 md:grid-cols-4 lg:grid-cols-8">
            {stages.map((s) => (
              <HealthIndicator
                key={s.stage_code}
                label={`${s.stage_code}`}
                value={s.current_p95}
                unit="ms"
                inverse
                thresholds={{ green: s.p95_target_ms, yellow: Math.round(s.p95_target_ms * 1.2), red: Math.round(s.p95_target_ms * 1.5) }}
              />
            ))}
          </div>

          <Card>
            <CardHeader><CardTitle>p95 atual vs alvo</CardTitle></CardHeader>
            <CardContent className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stages}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis dataKey="stage_code" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="current_p95" fill="hsl(var(--primary))" name="p95 atual" />
                  <Bar dataKey="p95_target_ms" fill="hsl(var(--muted-foreground))" name="alvo p95" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Top 10 turnos mais lentos (24h)</CardTitle></CardHeader>
            <CardContent>
              {slow.length === 0 ? (
                <p className="text-sm text-muted-foreground">Sem turnos registrados nas últimas 24h.</p>
              ) : (
                <ul className="space-y-2">
                  {slow.map((t) => (
                    <li key={t.turn_id} className="flex items-center justify-between text-sm border-b border-border pb-1">
                      <span className="font-mono">{t.turn_id.slice(0, 12)}…</span>
                      <Badge variant="outline">{t.total_ms} ms</Badge>
                      <span className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleTimeString("pt-BR")}</span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
